import React, {useRef, useState} from 'react';
import {Alert, Button, Card, Modal, Space, Typography, Upload} from 'antd';
import {CloseOutlined, FileExcelOutlined, SaveOutlined, UploadOutlined} from '@ant-design/icons';
import AgGrid from './common/AgGrid';
import * as XLSX from 'xlsx';
import {AG_GRID_LOCALE_TEXT, AG_GRID_PAGE_SIZE_OPTIONS} from '../config/agGridConfig';

const { Text } = Typography;

function ExcelUploadModal({
  isOpen,
  onClose,
  onSuccess,
  agGridColumnDefs,
  agGridDefaultColDef,
  fieldMapping,
  uploadApi,
  excelGuide,
  rowKey = 'key',
}) {
  const [excelData, setExcelData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const gridRef = useRef();

  const handleFileSelect = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        const rows = jsonData.slice(1);
        // 필드명 매핑
        const mapping = fieldMapping || [];
        const processedData = rows.map((row, index) => {
          const obj = { [rowKey]: index };
          mapping.forEach((field, fieldIndex) => {
            obj[field] = row[fieldIndex] || '';
          });
          return obj;
        });
        setExcelData(processedData);
        setError('');
        setTimeout(() => {
          if (gridRef.current && gridRef.current.api) {
            gridRef.current.api.sizeColumnsToFit();
          }
        }, 100);
      } catch (err) {
        setError('엑셀 파일을 읽는 중 오류가 발생했습니다: ' + err.message);
        setExcelData([]);
      }
    };
    reader.readAsArrayBuffer(file);
    return false;
  };

  const handleSave = async () => {
    if (excelData.length === 0) {
      setError('저장할 데이터가 없습니다.');
      return;
    }
    setLoading(true);
    setError('');
    setSuccessMessage('');
    try {
      await uploadApi(excelData);
      setSuccessMessage(`${excelData.length}개 항목이 성공적으로 등록되었습니다.`);
      if (onSuccess) onSuccess();
      setTimeout(() => { handleClose(); }, 2000);
    } catch (err) {
      setError('일괄등록 중 오류가 발생했습니다: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setExcelData([]);
    setError('');
    setSuccessMessage('');
    setLoading(false);
    onClose();
  };

  const onGridReady = (params) => {
    gridRef.current = params.api;
    params.api.sizeColumnsToFit();
  };

  const uploadProps = {
    beforeUpload: handleFileSelect,
    accept: '.xlsx,.xls',
    showUploadList: false,
  };

  return (
    <Modal
      title={
        <Space>
          <FileExcelOutlined />
          <span>엑셀 업로드</span>
        </Space>
      }
      open={isOpen}
      onCancel={handleClose}
      width={1200}
      footer={[
        <Button key="cancel" onClick={handleClose} icon={<CloseOutlined />}>
          취소
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={loading}
          disabled={excelData.length === 0}
          onClick={handleSave}
          icon={<SaveOutlined />}
        >
          {loading ? '저장 중...' : '저장'}
        </Button>
      ]}
    >
      {/* 성공 메시지 */}
      {successMessage && (
        <Alert
          message="성공"
          description={successMessage}
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* 에러 메시지 */}
      {error && (
        <Alert
          message="오류"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* 파일 선택 영역 */}
      <Card style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Upload {...uploadProps}>
            <Button icon={<UploadOutlined />} type="primary">
              엑셀 파일 선택
            </Button>
          </Upload>
          {excelData.length > 0 && (
            <Text type="secondary">
              {excelData.length}개 항목이 로드되었습니다.
            </Text>
          )}
        </Space>
      </Card>

      {/* 엑셀 파일 형식 안내 */}
      {excelGuide ? (
        <Card title="엑셀 파일 형식 안내" size="small" style={{ marginBottom: 16 }} headStyle={{ backgroundColor: '#fafafa' }}>
          <Text type="secondary">{excelGuide}</Text>
        </Card>
      ) : null}

      {/* 테이블 렌더링 */}
      {excelData.length > 0 && (
        <div style={{ height: '400px', width: '100%', marginBottom: '20px' }}>
          <AgGrid
            ref={gridRef}
            rowData={excelData}
            columnDefs={agGridColumnDefs}
            defaultColDef={agGridDefaultColDef}
            paginationPageSize={20}
            paginationPageSizeSelector={AG_GRID_PAGE_SIZE_OPTIONS}
            onCellValueChanged={(params) => {
              const updated = [...excelData];
              updated[params.rowIndex] = params.data;
              setExcelData(updated);
            }}
            onGridReady={onGridReady}
            localeText={AG_GRID_LOCALE_TEXT}
            domLayout="normal"
          />
        </div>
      )}
    </Modal>
  );
}

export default ExcelUploadModal;
